import { Modal } from 'antd';
import type { SystemDesignLossesResponse } from '../../../../services/oneButtonServices/designServices';
import { isEnfin } from './productsFinanceHubHelpers';
import { columnIncludesSolar } from './quoteCreationProducts';

export const DESIGN_LOSSES_MISMATCH_TITLE = 'Design losses mismatch';

/** Centered warning modal for Enfin / oneDRAW losses mismatch (replaces top-right notifications). */
export function showDesignLossesMismatchModal(
    message: string,
    theme: string,
    zIndex = 14000,
): void {
    Modal.warning({
        title: DESIGN_LOSSES_MISMATCH_TITLE,
        content: message,
        okText: 'OK',
        centered: true,
        zIndex,
        className: `obd-quote-design-blocked-modal obd-theme-${theme}`,
        rootClassName: `obd-theme-${theme}`,
    });
}

const ENFIN_PARTNER_GENERIC_LOSSES_MESSAGE =
    'The selected Finance Partner is Enfin, but the associated oneDRAW design is using Generic Losses. Please update the losses in oneDRAW and re-finalize the design before generating a quote.';

const NON_ENFIN_PARTNER_ENFIN_LOSSES_MESSAGE =
    'The selected Finance Partner does not use Enfin Losses, but the associated design in oneDRAW is currently configured with Enfin Losses. Please update the losses in oneDRAW and re-finalize the design before generating a quote.';

export type DesignLossesMismatchResult = {
    blocked: boolean;
    message?: string;
};

export type DesignLossesHubRow = {
    product_Type_Name?: string | null;
    finance_Partner_Display_Name?: string | null;
    activeFlag?: boolean;
};

export function isEnfinLossesSource(
    systemDesignLosses: SystemDesignLossesResponse | null | undefined,
): boolean {
    return String(systemDesignLosses?.LossesSource ?? '')
        .trim()
        .toLowerCase() === 'enfin';
}

export function getDesignLossesSourceLabel(
    lossesSource: string | null | undefined,
): 'Enfin losses' | 'oneDRAW losses' | null {
    const raw = String(lossesSource ?? '').trim();
    if (!raw) return null;
    return isEnfinLossesSource({ LossesSource: raw }) ? 'Enfin losses' : 'oneDRAW losses';
}

/** Enfin finance partner on Solar is only valid when the finalized design uses Enfin losses. */
export function enfinPartnerAllowedForDesignLosses(
    systemDesignLosses: SystemDesignLossesResponse | null | undefined,
): boolean {
    return isEnfinLossesSource(systemDesignLosses);
}

export const ENFIN_PARTNER_REQUIRES_ENFIN_LOSSES_MESSAGE =
    'Enfin is only available when the finalized oneDRAW design uses Enfin losses. Update losses in oneDRAW and re-finalize the design, or choose another finance partner.';

function hubRowHasAssignedPartner(row: { finance_Partner_Display_Name?: string | null }): boolean {
    const partner = String(row.finance_Partner_Display_Name ?? '').trim();
    return partner.length > 0 && partner.toLowerCase() !== 'select finance partner';
}

function activeSolarHubRow(rows: DesignLossesHubRow[]): DesignLossesHubRow | undefined {
    return rows.find(
        (r) => String(r.product_Type_Name ?? '') === 'Solar' && r.activeFlag === true,
    );
}

function solarRowFromOpportunity(opportunityDetails: unknown): DesignLossesHubRow | undefined {
    const products = (opportunityDetails as { OpportunityProduct?: unknown[] } | null)?.OpportunityProduct;
    if (!Array.isArray(products)) return undefined;
    const solar = products.find(
        (p) =>
            String((p as { Product_Type_Name?: string }).Product_Type_Name ?? '') === 'Solar' &&
            (p as { ActiveFlag?: boolean })?.ActiveFlag,
    );
    if (!solar) return undefined;
    return {
        product_Type_Name: 'Solar',
        activeFlag: true,
        finance_Partner_Display_Name: String(
            (solar as { Finance_Partner_Display_Name?: string }).Finance_Partner_Display_Name ?? '',
        ),
    };
}

/** Solar-only — skips when Solar is off or has no assigned partner. */
function evaluateSolarLossesMismatch(
    lossesSource: string | null | undefined,
    solarRow: DesignLossesHubRow | undefined,
): DesignLossesMismatchResult {
    const normalized = String(lossesSource ?? '').trim();
    if (!normalized) return { blocked: false };
    if (!solarRow?.activeFlag || !hubRowHasAssignedPartner(solarRow)) {
        return { blocked: false };
    }

    const isEnfinLoss = normalized.toLowerCase() === 'enfin';
    const solarPartnerIsEnfin = isEnfin(String(solarRow.finance_Partner_Display_Name ?? ''));

    if (solarPartnerIsEnfin && !isEnfinLoss) {
        return { blocked: true, message: ENFIN_PARTNER_GENERIC_LOSSES_MESSAGE };
    }
    if (!solarPartnerIsEnfin && isEnfinLoss) {
        return { blocked: true, message: NON_ENFIN_PARTNER_ENFIN_LOSSES_MESSAGE };
    }
    return { blocked: false };
}

/** Block quote generation when finalized design losses vendor does not match active Solar partner. */
export function evaluateDesignLossesMismatchForQuote(
    systemDesignLosses: SystemDesignLossesResponse | null | undefined,
    opportunityDetails: unknown,
    quoteProduct: string | undefined,
    hubRows?: DesignLossesHubRow[] | null,
): DesignLossesMismatchResult {
    if (!quoteProduct || !columnIncludesSolar(quoteProduct)) {
        return { blocked: false };
    }

    const lossesSource = systemDesignLosses?.LossesSource;
    if (hubRows && hubRows.length > 0) {
        return evaluateDesignLossesMismatchForHubSave(hubRows, lossesSource);
    }
    return evaluateSolarLossesMismatch(lossesSource, solarRowFromOpportunity(opportunityDetails));
}

/** Product save — validate active Solar partner against finalized design losses (no bypass). */
export function evaluateDesignLossesMismatchForHubSave(
    rows: DesignLossesHubRow[],
    lossesSource: string | null | undefined,
): DesignLossesMismatchResult {
    return evaluateSolarLossesMismatch(lossesSource, activeSolarHubRow(rows));
}
